import { TooltipTitle } from './TooltipTitle';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Barcode, FileText, ScanLine, Sparkles, Table2 } from 'lucide-react';
import { spotPage } from './working-progress';
import type { DocumentResult, OcrLine, PageRecognitionEvidence } from '../shared/desktop-api';

type EvidenceTask = {
  previewPath?: string | null;
  evidence?: PageRecognitionEvidence | null;
  document?: DocumentResult | null;
};
type PreviewIdentityTask = {
  previewPath?: string | null;
  label?: string | null;
  pageNumber?: number;
  document?: Pick<DocumentResult, 'sourceName' | 'pages'> | null;
};

/** Captions belong to the decoded page, not the next requested preview. */
export function learningPreviewIdentity(
  path: string | null | undefined,
  tasks: readonly PreviewIdentityTask[],
): { label: string; pageNumber?: number } {
  const labelOf = (value: string | null | undefined) => value?.trim() || 'Saved page preview';
  const pageOf = (value: number | undefined) =>
    Number.isSafeInteger(value) && value! > 0 ? value : undefined;
  if (!path) return { label: 'Saved page preview' };
  const task = tasks.find((item) => item.previewPath === path);
  if (task) return { label: labelOf(task.label), pageNumber: pageOf(task.pageNumber) };
  for (const item of tasks) {
    const page = item.document?.pages.find((candidate) => candidate.previewPath === path);
    if (page) return { label: labelOf(item.document?.sourceName), pageNumber: pageOf(page.number) };
  }
  return { label: 'Saved page preview' };
}

type Box = { x: number; y: number; width: number; height: number };
const bounds = (lines: OcrLine[]): Box | null => {
  const points = lines
    .flatMap((line) => line.polygon)
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  if (!points.length) return null;
  const x = Math.min(...points.map((p) => p.x)),
    y = Math.min(...points.map((p) => p.y));
  const width = Math.max(...points.map((p) => p.x)) - x,
    height = Math.max(...points.map((p) => p.y)) - y;
  return width > 0 && height > 0 ? { x, y, width, height } : null;
};

/** Exact preview identity is mandatory: a newly arrived page must never inherit
 * the preceding page's boxes, even when both belong to the same document. */
export function learningPageEvidence(
  path: string | null | undefined,
  tasks: readonly EvidenceTask[],
) {
  const pageDocument = path
    ? tasks.find((task) => task.document?.pages.some((page) => page.previewPath === path))?.document
    : undefined;
  const page = pageDocument?.pages.find((page) => page.previewPath === path);
  const early = path
    ? tasks.find((task) => task.evidence?.previewPath === path)?.evidence
    : undefined;
  const lines = page?.lines ?? early?.lines ?? [];
  const number = page?.number ?? early?.pageNumber;
  const fields =
    pageDocument?.fields
      .filter(
        (field) =>
          field.name !== 'Document type' &&
          field.value?.trim() &&
          field.evidencePageNumber === number,
      )
      .map((field) => ({
        name: field.name,
        value: field.value,
        verified: Boolean(field.verified),
        box: bounds(
          (field.evidenceLineIndexes ?? [])
            .map((index) => page?.lines[index])
            .filter((line): line is OcrLine => Boolean(line)),
        ),
      })) ?? [];
  const rows =
    pageDocument?.lineItems
      .filter((row) => row.pageNumber === number)
      .map((row) => ({
        number: row.rowNumber,
        box: bounds(
          (row.evidenceLineIndexes ?? [])
            .map((index) => page?.lines[index])
            .filter((line): line is OcrLine => Boolean(line)),
        ),
      })) ?? [];
  return {
    path,
    number,
    lines,
    lineCount: page?.lines.length ?? early?.lineCount ?? 0,
    barcodes: page?.barcodes ?? early?.barcodes ?? [],
    fields,
    rows,
    // How far the reader turned this page before reading it; the Read step turns
    // the preview back through the same angle. Zero when it was read as scanned.
    deskewDegrees: Number(early?.deskewDegrees ?? page?.deskewDegrees ?? 0) || 0,
    documentType: pageDocument?.documentType,
    startsNewDocument: page?.startsNewDocument,
    hasOcr: Boolean(page || early),
  };
}
type Evidence = ReturnType<typeof learningPageEvidence>;

/** How long a shown page must wait before the next replaces it, from when it was shown. */
export const previewDwellWait = (shownAt: number, dwellMilliseconds: number, now: number) =>
  Math.max(0, shownAt + dwellMilliseconds - now);

/** Retain the last decoded page during I/O, decoding, and recoverable read errors.
 * Callers derive evidence from the returned path, never the pending path.
 *
 * With a dwell, each page stays up at least that long before the newest one replaces it.
 * One page leaves before the next arrives (styles.css, owner 2026-09-28), so every swap
 * has a moment with no page; when the engine names pages several times a second (reading
 * a wave on the graphics card, saving a wave's results) the area was empty more often than
 * not, and screenshots caught it blank (October 3, 2026). Pages in between are skipped,
 * never invented: the one shown is always a page the engine named. */
export function useLearningPreview(path: string | null | undefined, dwellMilliseconds = 0) {
  const [loaded, setLoaded] = useState<{ path: string; source: string } | null>(null);
  const [due, setDue] = useState<string | null>(path ?? null);
  const shownAt = useRef(0);
  useEffect(() => {
    if (!path) return;
    const wait = previewDwellWait(shownAt.current, dwellMilliseconds, Date.now());
    if (wait === 0) {
      setDue(path);
      return;
    }
    const timer = window.setTimeout(() => setDue(path), wait);
    return () => window.clearTimeout(timer);
  }, [path, dwellMilliseconds]);
  useEffect(() => {
    let canceled = false;
    if (!due || !window.capturePointDesktop) return;
    void window.capturePointDesktop
      .readPagePreview(due)
      .then(async (source) => {
        const image = new Image();
        image.src = source;
        await image.decode();
        if (!canceled) {
          shownAt.current = Date.now();
          setLoaded({ path: due, source });
        }
      })
      .catch(() => {
        // A failed preview must not blank a successfully displayed page. It is no longer due,
        // so choosing that page again starts a new read; left due, choosing it again set the
        // same value and nothing was read (learning-preview-identity-tests, October 5, 2026).
        if (!canceled) setDue(null);
      });
    return () => {
      canceled = true;
    };
  }, [due]);
  return loaded;
}

type PreviewProps = { source: string; label: string; evidence: Evidence; stage: string };
/**
 * One page leaves before the next arrives (styles.css, owner 2026-09-28), and the page
 * leaving waits until the next one can really start to arrive. The browser holds a new
 * page's arrive animation "pending" until its large image is decoded and drawn; on a busy
 * processor (pages being read beside it) that took about 2 s, and the old page had already
 * faded, so the area sat empty (measured in the real app, October 4, 2026). The old page
 * keeps its own element (same key), so it is never decoded again on its way out.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function LearningEvidencePreview(props: PreviewProps) {
  const last = useRef(props);
  const [source, setSource] = useState(props.source);
  const [leaving, setLeaving] = useState<PreviewProps | null>(null);
  const [departing, setDeparting] = useState(false);
  const incoming = useRef<HTMLDivElement>(null);
  if (props.source !== source) {
    setSource(props.source);
    setLeaving(last.current);
    setDeparting(false);
  }
  useLayoutEffect(() => {
    last.current = props;
  });
  useLayoutEffect(() => {
    if (!leaving) return;
    let current = true;
    const go = () => {
      if (current) setDeparting(true);
    };
    const arriving = incoming.current?.getAnimations()[0];
    if (arriving) void arriving.ready.then(go, go);
    else go();
    return () => {
      current = false;
    };
  }, [leaving]);
  useEffect(() => {
    if (!departing) return;
    const timer = window.setTimeout(() => setLeaving(null), 360);
    return () => window.clearTimeout(timer);
  }, [departing]);
  return (
    <div className="learning-evidence-transition">
      {leaving && (
        <div
          key={leaving.source}
          className={`learning-evidence-outgoing${departing ? ' departing' : ''}`}
          aria-hidden="true"
        >
          <EvidencePage {...leaving} />
        </div>
      )}
      <div key={props.source} ref={incoming} className="learning-evidence-incoming">
        <EvidencePage {...props} />
      </div>
    </div>
  );
}

/** Seconds the straightening turn takes, when the page was turned at all. */
const STRAIGHTEN_S = 0.6;
/** Seconds the text blocks take to be outlined, top to bottom. */
const SWEEP_S = 1.2;
/** Seconds one barcode's laser pass takes. */
const LASER_S = 1.1;

/** A small dark tag over a box, kept on the page. */
function Tag({
  box,
  text,
  font,
  width,
  delay,
}: {
  box: Box;
  text: string;
  font: number;
  width: number;
  delay: number;
}) {
  const tagWidth = (text.length * 0.6 + 1.4) * font;
  const x = Math.max(0, Math.min(box.x, width - tagWidth));
  const y = box.y - font * 2.1 >= 0 ? box.y - font * 2.1 : box.y + box.height + font * 0.3;
  return (
    <g className="reading-tag" style={{ animationDelay: `${delay.toFixed(2)}s` }}>
      <rect
        x={x}
        y={y}
        width={tagWidth}
        height={font * 1.75}
        style={{ rx: font * 0.4, ry: font * 0.4 } as CSSProperties}
      />
      <text x={x + font * 0.7} y={y + font * 1.22} fontSize={font}>
        {text}
      </text>
    </g>
  );
}

/**
 * The Read step's page: the real work on the real page, in order. The page turns
 * from the tilt it was scanned at to straight (by the engine's own angle); the
 * blocks of text it read are outlined top to bottom; each table lights up with its
 * rows ruled and a tag; the title, reference numbers, dates and total are ringed
 * and named; each decoded barcode gets a laser pass and its value. Everything drawn
 * comes from the engine's lines and codes for this page (spotPage in working-progress.ts); only the
 * timing is the screen's. With reduced motion, the finished state is shown at once.
 */
function ReadingOverlay({ evidence, width }: { evidence: Evidence; width: number }) {
  const turned = Math.abs(evidence.deskewDegrees) >= 0.05;
  const start = turned ? STRAIGHTEN_S : 0;
  const page = useMemo(() => spotPage(evidence.lines), [evidence.lines]);
  const blocks = page.blocks
    .slice()
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .slice(0, 60);
  const codes = evidence.barcodes
    .slice(0, 6)
    .filter((code) => code.width > 0 && code.height > 0 && code.value?.trim());
  const font = Math.max(16, width / 44);
  const pad = Math.max(4, font * 0.35);
  const grow = (box: Box, by = pad): Box => ({
    x: box.x - by,
    y: box.y - by,
    width: box.width + by * 2,
    height: box.height + by * 2,
  });
  const tablesAt = start + SWEEP_S;
  const spotsAt = tablesAt + page.tables.length * 0.45 + 0.1;
  const laserAt = spotsAt + page.spots.length * 0.3 + 0.1;
  return (
    <>
      <g className="reading-blocks">
        {blocks.map((block, index) => {
          const box = grow(block);
          return (
            <rect
              key={index}
              {...box}
              style={
                {
                  rx: pad,
                  ry: pad,
                  animationDelay: `${(start + (index / Math.max(1, blocks.length)) * SWEEP_S).toFixed(2)}s`,
                } as CSSProperties
              }
            />
          );
        })}
      </g>
      <g className="reading-tables">
        {page.tables.map((table, index) => {
          const box = grow(table.box, pad * 2);
          const delay = tablesAt + index * 0.45;
          return (
            <g key={index} style={{ animationDelay: `${delay.toFixed(2)}s` }}>
              <rect
                className="reading-table-frame"
                {...box}
                style={{ rx: pad * 1.5, ry: pad * 1.5 } as CSSProperties}
              />
              {table.rows.slice(1).map((row, rowIndex) => {
                const above = table.rows[rowIndex];
                const y = (above.y + above.height + row.y) / 2;
                return (
                  <line
                    key={rowIndex}
                    className="reading-table-rule"
                    x1={box.x + pad}
                    x2={box.x + box.width - pad}
                    y1={y}
                    y2={y}
                  />
                );
              })}
              <Tag
                box={box}
                text={`Table · ${table.rows.length} rows`}
                font={font}
                width={width}
                delay={delay + 0.15}
              />
            </g>
          );
        })}
      </g>
      <g className="reading-spots">
        {page.spots.map((spot, index) => {
          const box = grow(spot.box, pad * 1.4);
          const delay = spotsAt + index * 0.3;
          return (
            <g
              key={index}
              className={`reading-spot-mark reading-spot-${spot.kind}`}
              style={{ animationDelay: `${delay.toFixed(2)}s` }}
            >
              <rect {...box} style={{ rx: pad, ry: pad } as CSSProperties} />
              <Tag
                box={box}
                text={spot.label}
                font={font * 0.85}
                width={width}
                delay={delay + 0.1}
              />
            </g>
          );
        })}
      </g>
      <g className="reading-barcodes">
        {codes.map((code, index) => {
          const delay = laserAt + index * (LASER_S + 0.3);
          const value = code.value.trim().slice(0, 40);
          const labelWidth = (value.length * 0.62 + 1.6) * font;
          const right = code.x + code.width + font * 0.6 + labelWidth <= width;
          const labelX = right ? code.x + code.width + font * 0.6 : Math.max(0, code.x);
          const labelY = right
            ? code.y + code.height / 2 - font * 0.95
            : Math.max(0, code.y - font * 2.3);
          const codePad = Math.max(6, code.height * 0.12);
          return (
            <g key={index}>
              <rect
                className="reading-barcode-frame"
                x={code.x - codePad}
                y={code.y - codePad}
                width={code.width + codePad * 2}
                height={code.height + codePad * 2}
                style={
                  {
                    rx: codePad,
                    ry: codePad,
                    animationDelay: `${delay.toFixed(2)}s`,
                  } as CSSProperties
                }
              />
              <rect
                className="reading-laser"
                x={code.x}
                y={code.y - codePad * 1.6}
                width={Math.max(3, width / 420)}
                height={code.height + codePad * 3.2}
                style={
                  {
                    ['--laser-run' as string]: `${code.width}px`,
                    animationDelay: `${delay.toFixed(2)}s`,
                    animationDuration: `${LASER_S}s`,
                  } as CSSProperties
                }
              />
              <g
                className="reading-barcode-label"
                style={{ animationDelay: `${(delay + LASER_S * 0.85).toFixed(2)}s` }}
              >
                <rect
                  x={labelX}
                  y={labelY}
                  width={labelWidth}
                  height={font * 1.9}
                  style={{ rx: font * 0.45, ry: font * 0.45 } as CSSProperties}
                />
                <text x={labelX + font * 0.8} y={labelY + font * 1.3} fontSize={font}>
                  {value}
                </text>
                <TooltipTitle>
                  {code.symbology}: {code.value}
                </TooltipTitle>
              </g>
            </g>
          );
        })}
      </g>
    </>
  );
}

function EvidencePage({ source, label, evidence, stage }: PreviewProps) {
  const [size, setSize] = useState<{ source: string; width: number; height: number } | null>(null);
  const boxes = useMemo(
    () => evidence.lines.slice(0, 120).map((line) => ({ box: bounds([line]), text: line.text })),
    [evidence.lines],
  );
  const ready = size?.source === source && size.width > 0 && size.height > 0;
  if (stage === 'recognizing') {
    const turned = Math.abs(evidence.deskewDegrees) >= 0.05;
    return (
      <div
        className={`learning-evidence-page evidence-phase-recognizing reading-page${turned ? ' reading-page-turned' : ''}`}
        style={
          turned
            ? ({
                ['--reading-skew' as string]: `${(-evidence.deskewDegrees).toFixed(2)}deg`,
              } as CSSProperties)
            : undefined
        }
      >
        <img
          src={source}
          alt={label}
          onLoad={(event) =>
            setSize({
              source,
              width: event.currentTarget.naturalWidth,
              height: event.currentTarget.naturalHeight,
            })
          }
        />
        {ready && evidence.hasOcr && (
          <svg
            className="learning-evidence-overlay reading-overlay"
            viewBox={`0 0 ${size.width} ${size.height}`}
            preserveAspectRatio="xMidYMid meet"
            aria-label="Text and barcodes read on this page"
            role="img"
          >
            <ReadingOverlay evidence={evidence} width={size.width} />
          </svg>
        )}
      </div>
    );
  }
  return (
    <div className={`learning-evidence-page evidence-phase-${stage}`}>
      <img
        src={source}
        alt={label}
        onLoad={(event) =>
          setSize({
            source,
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
      />
      {ready && (
        <svg
          className="learning-evidence-overlay"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="xMidYMid meet"
          aria-label="Recognition locations on this page"
          role="img"
        >
          <g className="evidence-text-regions">
            {boxes.map(
              ({ box, text }, index) =>
                box && <rect key={index} {...box} className="evidence-text-region" />,
            )}
          </g>
          <g className="evidence-table-regions">
            {evidence.rows
              .slice(0, 24)
              .map(
                (row, index) =>
                  row.box && (
                    <rect
                      key={row.number}
                      {...row.box}
                      style={{ animationDelay: `${0.5 + index * 0.09}s` }}
                    />
                  ),
              )}
          </g>
          <g className="evidence-field-regions">
            {evidence.fields.slice(0, 10).map(
              (field, index) =>
                field.box && (
                  <g key={field.name} style={{ animationDelay: `${0.2 + index * 0.22}s` }}>
                    <rect {...field.box} />
                    <text
                      x={field.box.x}
                      y={Math.max(size.width / 65, field.box.y - 5)}
                      fontSize={size.width / 65}
                    >
                      {field.name.slice(0, 24)}
                    </text>
                  </g>
                ),
            )}
          </g>
          <g className="evidence-barcode-regions">
            {evidence.barcodes
              .slice(0, 12)
              .filter((code) => code.width > 0 && code.height > 0)
              .map((code, index) => (
                <g key={index}>
                  <rect x={code.x} y={code.y} width={code.width} height={code.height} />
                  <TooltipTitle>
                    {code.symbology}: {code.value}
                  </TooltipTitle>
                  <text
                    x={code.x}
                    y={Math.max(size.width / 60, code.y - 7)}
                    fontSize={size.width / 60}
                  >
                    {code.symbology}
                  </text>
                </g>
              ))}
          </g>
        </svg>
      )}
      {evidence.hasOcr && (
        <div className="learning-evidence-key">
          <span>
            <ScanLine size={11} />
            Text
          </span>
          {evidence.barcodes.length > 0 && (
            <span>
              <Barcode size={11} />
              Barcodes
            </span>
          )}
          {evidence.fields.some((field) => field.box) && (
            <span>
              <Sparkles size={11} />
              Fields
            </span>
          )}
          {evidence.rows.some((row) => row.box) && (
            <span>
              <Table2 size={11} />
              Rows
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function LearningEvidenceSummary({
  evidence,
  stage,
  pageCount,
  documentCount,
  pageScope = 'sample',
}: {
  evidence: Evidence;
  stage: string;
  pageCount: number;
  documentCount: number;
  pageScope?: 'sample' | 'wave';
}) {
  const text = evidence.lines.filter((line) => line.text.trim()).slice(0, 4);
  return (
    <section className="learning-evidence-summary" aria-label="Live evidence from the preview page">
      <header>
        <ScanLine size={14} />
        <strong>{evidence.number ? `On page ${evidence.number}` : 'Evidence arriving'}</strong>
        <small>LIVE</small>
      </header>
      <div className="evidence-milestones">
        <span className={evidence.path ? 'arrived' : ''}>
          <FileText size={14} />
          <b>{pageCount.toLocaleString()}</b>
          <small>pages in this {pageScope}</small>
        </span>
        <span className={evidence.hasOcr ? 'arrived' : ''}>
          <ScanLine size={14} />
          <b>{evidence.hasOcr ? evidence.lineCount : '-'}</b>
          <small>text lines here</small>
        </span>
        <span className={documentCount ? 'arrived' : ''}>
          <Table2 size={14} />
          <b>{documentCount || '-'}</b>
          <small>documents grouped</small>
        </span>
      </div>
      {stage === 'organizing' && (
        <div className="evidence-grouping">
          <FileText size={17} />
          <span>
            Pages → documents
            <small>
              {evidence.startsNewDocument
                ? 'A document starts on this page'
                : 'Comparing page starts and continuations'}
            </small>
          </span>
        </div>
      )}
      {text.length > 0 ? (
        <div className="evidence-reading">
          <small>TEXT LOCATED</small>
          {text.map((line, index) => (
            <div key={`${evidence.path}-${index}`}>
              <i />
              <span>{line.text}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="evidence-awaiting">
          <ScanLine size={18} />
          <span>
            {evidence.path ? 'Page image ready' : 'Opening your pages'}
            <small>Text locations appear when reading finishes.</small>
          </span>
        </div>
      )}
      {evidence.barcodes.slice(0, 3).map((code, index) => (
        <div className="evidence-barcode-receipt" key={index}>
          <Barcode size={23} />
          <span>
            <small>{code.symbology} detected</small>
            <strong>{code.value}</strong>
          </span>
        </div>
      ))}
      {evidence.documentType && (
        <div className="evidence-type-receipt">
          <Sparkles size={15} />
          <span>
            <small>Suggested document type</small>
            <strong>{evidence.documentType}</strong>
          </span>
        </div>
      )}
      {evidence.fields.length > 0 && (
        <div className="evidence-field-receipts">
          <small>VALUES FOUND ON THIS PAGE</small>
          {evidence.fields.slice(0, 6).map((field) => (
            <div key={field.name}>
              <span>
                {field.name}
                <small>
                  {field.verified
                    ? 'Confirmed'
                    : field.box
                      ? 'Source located · unconfirmed'
                      : 'Source location pending'}
                </small>
              </span>
              <strong>{field.value}</strong>
            </div>
          ))}
        </div>
      )}
      {evidence.rows.length > 0 && (
        <div className="evidence-row-receipt">
          <Table2 size={15} />
          <span>
            {evidence.rows.length} line items on this page
            <small>{evidence.rows.filter((row) => row.box).length} with source locations</small>
          </span>
        </div>
      )}
    </section>
  );
}
